/*
 * File: CustomSlackTimeline.tsx
 * Project: a-a
 * File Created: Wednesday, 30th July 2025 10:51:34 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Friday, 22nd August 2025 10:43:08 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import React, { useState } from 'react';
import queryString from 'query-string';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import zeplinColor from '../../../../theme/Color';
import CustomCard from '../../CustomCard/CustomCard';
import { LightTooltip } from '../../CustomTooltip/CustomTooltip';
import { ReactComponent as SlackUser } from '../../../../assets/slack-user.svg';
import style from './CustomSlackTimeline.module.css';
import CustomButton from '../../CustomButton/CustomButton';
import { Link, useLocation } from 'react-router-dom';

const SlackMessageCard = ({
  message,
  isExpanded,
  newDataTotal,
  index,
  isLast = false,
  isFirst = false,
  total,
  handleCardClick,
  isLoginBtn = false,
  setShowSlackConnect,
}: {
  message: any;
  isExpanded: boolean;
  newDataTotal: number;
  index: number;
  isLast: boolean;
  isFirst: boolean;
  total: number;
  handleCardClick: () => void;
  isLoginBtn?: boolean;
  setShowSlackConnect?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const theme = useTheme();
  const location = useLocation();
  console.log(isFirst, total, 'isFirst and total');
  const [anchorEl, setAnchorEl] = React.useState<HTMLImageElement | null>(null);
  const [copy, setCopy] = useState('');
  // Responsive breakpoints for text length
  const isXs = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 900px - 1200px
  const isLg = useMediaQuery(theme.breakpoints.between('lg', 'xl')); // 1200px - 1536px
  // xl and above: > 1536px

  // Get responsive long text threshold for additional height
  const getLongTextThreshold = () => {
    if (isXs) return 80; // Mobile
    if (isSm) return 90; // Small tablet
    if (isMd) return 100; // Large tablet
    if (isLg) return 110; // Desktop
    return 170; // Large desktop
  };
  const getCardHeight = () => {
    const longTextThreshold = getLongTextThreshold();
    if (isLoginBtn) return isLg || isMd || isXs || isSm ? '115px' : '128px';

    if (isFirst) {
      // First card: base height + channel section + notification count
      let height = 80; // Base height
      if (message?.channel) height += 35; // Channel section
      if (message?.text && message.text.length > longTextThreshold) height += 15; // Long text
      if (total > 1 && !isExpanded) height += 35;
      return `${height}px`;
    } else {
      // Subsequent cards: just sender + text + time
      let height = 80; // Base height for subsequent cards
      if (!isExpanded) {
        if (message?.channel) height += 25; // Channel section
        if (total > 1) height += 35;
        if (message?.text && message.text.length > longTextThreshold) height += 15; // Long text
        return `${height}px`;
      }
      if (message?.text && message.text.length > longTextThreshold) height += 15; // Long text
      return `${height}px`;
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLImageElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'slack-popover' : undefined;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(message.account);
    setCopy('Copied Message');
  };

  const handleClose = () => {
    setCopy('');
  };

  // Format time display
  const now = new Date();
  const eventDate = new Date(message.date);
  const timeDifference = now.getTime() - eventDate.getTime();
  const minutesAgo = Math.floor(timeDifference / 1000 / 60);
  const hoursAgo = Math.floor(timeDifference / 1000 / 60 / 60);

  let timeText = '';
  if (hoursAgo > 23) {
    timeText = eventDate?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } else if (hoursAgo > 0) {
    timeText = `${hoursAgo}h ago`;
  } else if (minutesAgo > 0) {
    timeText = `${minutesAgo}m ago`;
  } else {
    timeText = 'Just now';
  }

  return (
    <CustomCard
      styles={{
        borderRadius: '14px',
        backgroundColor: isLoginBtn
          ? '#4956F433'
          : index < newDataTotal || message?.new
          ? 'rgba(73, 86, 244, 0.1)'
          : zeplinColor.Neutral3,
        padding: { xs: '12px', xl: '16px' },
        // gap: { xs: '8px', xl: '12px' },
        ...(isLoginBtn && { gap: { xs: '8px', xl: '12px' } }),
        display: 'flex',
        flexDirection: 'column',
        // justifyContent: 'space-between',
        width: '100%',
        // height: isLoginBtn ? '128px' : isExpanded ? 'auto' : '150px',
        height: getCardHeight(),
        minHeight: getCardHeight(),
        // marginBottom: '1rem',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isLoginBtn ? (
            <Typography variant='responsiveHeading4'>Connect Slack to Get Started</Typography>
          ) : (
            <Typography
              variant='responsiveBody2'
              fontWeight='500'
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.4,
                // height: '2.8em', // Enforce exact height for 2 lines (2 * 1.4em)
                maxWidth: {
                  xs: message?.isEdited ? '660px' : '700px',
                  sm: message?.isEdited ? '760px' : '800px',
                  md: message?.isEdited ? '860px' : '900px',
                  lg: message?.isEdited ? '1000px' : '1050px',
                  xl: message?.isEdited ? '1260px' : '1350px',
                },
                // wordBreak: 'break-all', // This helps prevent partial words
                position: 'relative',
              }}
            >
              {message?.account}{' '}
              {message?.isEdited && (
                <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                  (edited)
                </Typography>
              )}
            </Typography>
          )}
        </Box>

        {isLoginBtn ? (
          <img
            src={'/assets/close-icon.svg'}
            className={style['icon-18']}
            alt='close'
            style={{ cursor: 'pointer' }}
            onClick={() => setShowSlackConnect && setShowSlackConnect(false)}
          />
        ) : (
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Typography
              variant='responsiveBody3'
              color={index < newDataTotal ? zeplinColor.Brand1 : zeplinColor.Neutral10}
              sx={{ marginRight: '4px' }}
            >
              {timeText}
            </Typography>

            {/* <LightTooltip
              title={
                <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                  Options
                </Typography>
              }
              placement='bottom-end'
              style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
            >
              <img
                className={style['vertical-dots']}
                src={'/assets/vertical-dots.svg'}
                alt='options-icon'
                onClick={handleClick}
                aria-hidden='false'
              />
            </LightTooltip> */}
          </Box>
        )}
      </Box>
      {isFirst && !isLoginBtn && (
        <Box
          sx={{
            padding: '4px 8px',
            borderRadius: '50px',
            display: 'flex',
            gap: '4px',
            marginTop: '4px',
            alignItems: 'center',
            backgroundColor: zeplinColor.Neutral4,
            width: 'fit-content',
          }}
        >
          <img src={'/assets/slack-channel-lock.svg'} style={{ width: '14px', height: '14px' }} />
          <Typography variant='label1' color={zeplinColor.BasicBlack}>
            {message?.channel || '-'}
          </Typography>
        </Box>
      )}
      {/* <Divider sx={{ borderColor: zeplinColor.Neutral5 }} /> */}

      {isLoginBtn ? (
        ''
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            marginTop: '8px',
          }}
        >
          {/* <SlackUser className={style['slack-user']} /> */}
          <img src={message?.sender_img} className={style['slack-user']} />
          <Typography
            variant='responsiveBody3'
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.4,
              // height: '2.8em', // Enforce exact height for 2 lines (2 * 1.4em)
              maxWidth: { xs: '170px', sm: '250px', md: '300px', lg: '400px' },
            }}
          >
            {message?.sender}
          </Typography>
        </Box>
      )}
      {isLoginBtn ? (
        <Typography variant='responsiveBody3'>
          Connect your Slack for real-time messaging, notifications and collaboration within Huddle
        </Typography>
      ) : (
        ''
      )}
      {isFirst && total > 1 && !isExpanded && (
        <Typography
          variant='responsiveBody3'
          color={zeplinColor.Brand1}
          onClick={() => handleCardClick()}
          sx={{
            marginTop: '4px',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.4,
            // height: '2.8em', // Enforce exact height for 2 lines (2 * 1.4em)
            // maxWidth: { xs: '170px', sm: '250px', md: '300px', lg: '400px' },
          }}
        >
          {isExpanded ? 'Show less' : `${total - 1} more notification${total - 1 > 1 ? 's' : ''}`}
        </Typography>
      )}
      {isLoginBtn && (
        <Link
          // to={`https://slack.com/oauth?client_id=1547360932450.9088642262659&user_scope=%27channels%3Aread%2Cchannels%3Ahistory%2Cgroups%3Aread%2Cgroups%3Awrite%2Cchat%3Awrite%2Cgroups%3Ahistory%2Cim%3Aread%2Cim%3Ahistory%2Cmpim%3Aread%2Cmpim%3Ahistory%27&redirect_uri=${process.env.REACT_APP_REDIRECT}`}
          to={`https://slack.com/oauth/v2/authorize?${queryString.stringify({
            client_id: '1547360932450.9088642262659',
            // scope: 'channels:manage,channels:read,channels:history,groups:read,groups:write,groups:history,im:read,im:history,mpim:read,mpim:history',
            user_scope:
              'users:read,channels:read,channels:history,groups:read,groups:write,chat:write,groups:history,im:read,im:history,mpim:read,mpim:history',
            redirect_uri: `${process.env.REACT_APP_REDIRECT}`,
          })}`}
          // target='_blank'
          // rel='noopener noreferrer'
          style={{ color: zeplinColor.Neutral1, textDecoration: 'none', width: '78px' }}
          onClick={() => {
            // Set session storage flag before redirecting
            sessionStorage.setItem('slackConnecting', 'true');
            // Optionally notify the parent component
            // if (setShowSlackConnect) {
            //   setShowSlackConnect(false);
            // }
          }}
        >
          <CustomButton
            disabled={false}
            onClick={() => {
              sessionStorage.setItem('sf_redirect', 'Activity');
              sessionStorage.setItem('sf_path', location?.pathname);
            }}
            variant='contained'
            color={'primary'}
            buttonVariant='buttonSmall'
            style={{
              width: '100%',
              borderRadius: '8px',
              height: '32px',
              padding: '6px 12px',
              '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
            }}
            textStyle={{ color: zeplinColor.Neutral1 }}
            text={'Connect'}

            // tabindex={4}
          />
        </Link>
      )}
    </CustomCard>
  );
};

export default SlackMessageCard;
